import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { Toaster } from "react-hot-toast";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./store/store";
import { initObservability, getSentry } from "./observability";
import reportWebVitals from "./reportWebVitals";

const isIgnoredRuntimeError = (errorOrMessage) => {
  const message =
    typeof errorOrMessage === "string"
      ? errorOrMessage
      : errorOrMessage?.message || errorOrMessage?.reason?.message || "";

  // Sentry's onboarding test error (often injected by browser extensions or
  // pasted into the console). It explicitly says "ignore me" — drop it
  // everywhere so it never floods the console or Sentry.
  if (message.includes("Sentry test error - ignore me")) return true;

  // Browser/environment storage failure, not an app bug: Chrome throws this
  // when it cannot open the IndexedDB backing store (corrupted profile,
  // restricted storage, some incognito/extension states). Firebase Auth falls
  // back to in-memory persistence, so the survey still works — drop the noisy
  // unhandled rejection so it doesn't flood Sentry.
  if (message.includes("Internal error opening backing store for indexedDB.open")) {
    return true;
  }

  return false;
};

const installRuntimeErrorFilter = () => {
  const ignoreRuntimeError = (event) => {
    if (!isIgnoredRuntimeError(event.error || event.reason || event.message)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  };

  window.addEventListener("error", ignoreRuntimeError, true);
  window.addEventListener("unhandledrejection", ignoreRuntimeError, true);
};

installRuntimeErrorFilter();
initObservability();

const applyHebrewFont = () => {
  const hebrewRegex = /[\u0590-\u05FF]/;
  const SKIP_SELECTORS = '.survey-viewer, .survey-designer, .theme-editor-preview';
  const HEBREW_FONT = "'Noto Sans Hebrew', 'Heebo', 'Assistant', sans-serif";

  const shouldSkip = (el) => el && (el.closest?.(SKIP_SELECTORS) || el.matches?.(SKIP_SELECTORS));

  const checkElement = (element) => {
    if (element.nodeType === Node.TEXT_NODE) {
      const parent = element.parentElement;
      if (!parent || parent.classList.contains('auto-hebrew-font') || shouldSkip(parent)) return;
      const text = element.textContent;
      if (!text || !hebrewRegex.test(text)) return;
      const clean = text.replace(/\s+/g, '');
      if (clean.length === 0) return;
      const hebrewCount = (clean.match(/[\u0590-\u05FF]/g) || []).length;
      if (hebrewCount / clean.length > 0.3) {
        parent.classList.add('auto-hebrew-font', 'force-hebrew-font');
        parent.style.setProperty('font-family', HEBREW_FONT, 'important');
      }
    } else if (element.nodeType === Node.ELEMENT_NODE && !shouldSkip(element)) {
      for (const child of element.childNodes) checkElement(child);
    }
  };

  const root = document.getElementById('root');
  if (!root) return;
  checkElement(root);

  let debounceTimer = null;
  const observer = new MutationObserver((mutations) => {
    if (debounceTimer) return;
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) {
            checkElement(node);
          }
        }
        if (mutation.type === 'characterData' && mutation.target.nodeType === Node.TEXT_NODE) {
          checkElement(mutation.target);
        }
      }
    }, 200);
  });

  observer.observe(root, { childList: true, subtree: true, characterData: true });
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  // <React.StrictMode>
    <Provider store={store}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
      <Toaster 
        position="top-right" 
        reverseOrder={false}
        containerId="toast-container"
        containerStyle={{
          position: 'fixed',
          top: 70,
          right: 0,
          zIndex: 99999,
        }}
        toastOptions={{
          style: {
            marginRight: "50px",
            zIndex: 99999,
          }
        }}
      />
    </Provider>
  // </React.StrictMode>
);

setTimeout(applyHebrewFont, 300);

reportWebVitals((metric) => {
  const sentry = getSentry();
  if (sentry && typeof sentry.captureMessage === "function") {
    sentry.captureMessage(`web-vital:${metric.name}`, {
      level: "info",
      extra: { value: metric.value, id: metric.id, rating: metric.rating },
    });
  } else if (process.env.NODE_ENV === "development") {
    console.debug("[web-vitals]", metric.name, Math.round(metric.value));
  }
});
